/**
 * Projects Gallery - Parallax Columns
 *
 * Parallax behaviour:
 * - >= 768px and < 1024px (2 columns):
 *   left column moves up faster than right column
 * - >= 1024px and <= 1920px (3 columns):
 *   left and right columns move up faster than center column
 * - > 1920px, Container off, and 13+ items (4 columns):
 *   columns 1 & 3 move up faster; columns 2 & 4 use the slower offset
 *
 * Uses the CSS `translate` property (not `transform`) on `.project_item`
 * so it composes with GSAP tweens that write `transform` on the same items.
 */

(() => {
  const blocksOnPage = window.SOJ_BLOCKS_ON_PAGE;

  // If block tracking is available and this block isn't on the page, bail early
  if (
    Array.isArray(blocksOnPage) &&
    !blocksOnPage.includes("projects-gallery")
  ) {
    return;
  }

  // Respect reduced motion preferences
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );
  if (prefersReducedMotion.matches) {
    return;
  }

  // Don't run inside wp-admin/editor
  if (document.body && document.body.classList.contains("wp-admin")) {
    return;
  }

  function initProjectsGalleryParallax(section) {
    const useContainer = section.dataset.container !== "false";

    const state = {
      section,
      items: [],
      numColumns: 1,
      columnSpeeds: [],
      sectionTop: 0,
      sectionHeight: 0,
    };

    function refreshItems() {
      state.items = Array.from(section.querySelectorAll(".project_item"));
    }

    function updateLayout() {
      refreshItems();
      if (!state.items.length) return;

      const width =
        window.innerWidth || document.documentElement.clientWidth || 0;

      if (!useContainer && width > 1920 && state.items.length > 12) {
        state.numColumns = 4;
      } else if (width >= 1024) {
        state.numColumns = 3;
      } else if (width >= 768) {
        state.numColumns = 2;
      } else {
        state.numColumns = 1;
      }

      const rect = section.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;
      state.sectionTop = rect.top + scrollY;
      state.sectionHeight = rect.height;

      // Configure per-column speed factors
      state.columnSpeeds = [];
      for (let i = 0; i < state.numColumns; i++) {
        let speed = 0;

        if (state.numColumns === 2) {
          // Tablet: left column faster than right column
          speed = i === 0 ? 0.25 : 0.15;
        } else if (state.numColumns === 3) {
          // Desktop: left/right faster than center
          speed = i === 1 ? 0.12 : 0.25;
        } else if (state.numColumns === 4) {
          // Columns 2 & 4 (1-based) use the slower offset; 1 & 3 move faster
          speed = i === 1 || i === 3 ? 0.12 : 0.25;
        }

        state.columnSpeeds.push(speed);
      }
    }

    let ticking = false;
    let isCleared = false;

    function applyParallax() {
      ticking = false;

      const width =
        window.innerWidth || document.documentElement.clientWidth || 0;

      // Disable parallax below 768px or if we only have one column
      if (width < 768 || state.numColumns === 1) {
        if (!isCleared) {
          state.items.forEach((item) => {
            item.style.translate = "";
          });
          isCleared = true;
        }
        return;
      }
      isCleared = false;

      const scrollY = window.scrollY || window.pageYOffset || 0;
      const baseOffset = scrollY - state.sectionTop;

      state.items.forEach((item, index) => {
        const colIndex = state.numColumns > 0 ? index % state.numColumns : 0;
        const speed = state.columnSpeeds[colIndex] || 0;

        if (!speed) {
          item.style.translate = "";
          return;
        }

        const offset = -baseOffset * speed;
        item.style.translate = `0 ${offset.toFixed(2)}px`;
      });
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(applyParallax);
      }
    }

    function onResize() {
      updateLayout();
      isCleared = false;
      applyParallax();
    }

    updateLayout();
    applyParallax();

    section._sojGalleryParallaxRefresh = () => {
      updateLayout();
      isCleared = false;
      applyParallax();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
  }

  function init() {
    const sections = document.querySelectorAll(".projects-gallery");
    if (!sections.length) return;

    sections.forEach((section) => {
      if (section.dataset.parallax === "false") {
        return;
      }
      initProjectsGalleryParallax(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/**
 * Projects Gallery - Load more (AJAX pagination)
 */
(() => {
  const blocksOnPage = window.SOJ_BLOCKS_ON_PAGE;

  if (
    Array.isArray(blocksOnPage) &&
    !blocksOnPage.includes("projects-gallery")
  ) {
    return;
  }

  if (document.body && document.body.classList.contains("wp-admin")) {
    return;
  }

  function getAjaxUrl(section) {
    const dataUrl = section.dataset.ajaxUrl;
    if (dataUrl) {
      return dataUrl;
    }
    if (window.sojTheme && window.sojTheme.ajaxUrl) {
      return window.sojTheme.ajaxUrl;
    }
    return "/wp-admin/admin-ajax.php";
  }

  function initProjectsGalleryLoadMore(section) {
    const list = section.querySelector("[data-projects-gallery-list]");
    const loadMoreWrap = section.querySelector(
      "[data-projects-gallery-load-more-wrap]",
    );
    const loadMoreButton = section.querySelector(
      "[data-projects-gallery-load-more]",
    );

    if (!list) {
      return;
    }

    const MOBILE_MAX = 767;
    const perPageDesktop = parseInt(section.dataset.perPage, 10) || 6;
    const perPageMobile = parseInt(section.dataset.perPageMobile, 10) || 3;
    const paginated = section.dataset.whatToShow !== "selected";

    let isFetching = false;
    let foundPosts = parseInt(section.dataset.foundPosts, 10) || 0;
    let mobileRevealed = perPageMobile;
    let hasMore = section.dataset.hasMore === "1";

    const isMobile = () =>
      (window.innerWidth || document.documentElement.clientWidth || 0) <=
      MOBILE_MAX;

    const getPerPage = () => (isMobile() ? perPageMobile : perPageDesktop);

    const getItems = () => Array.from(list.querySelectorAll(".project_item"));

    const visibleCount = () => {
      const items = getItems();
      if (!isMobile()) {
        return items.length;
      }
      return items.filter((item) => !item.classList.contains("is-mobile-hidden"))
        .length;
    };

    const setHasMore = (value) => {
      hasMore = Boolean(value);
      section.dataset.hasMore = hasMore ? "1" : "0";
      if (loadMoreWrap) {
        loadMoreWrap.classList.toggle("is-hidden", !hasMore);
      }
      if (loadMoreButton) {
        loadMoreButton.disabled = !hasMore || isFetching;
      }
    };

    const refreshAfterChange = (newItems = []) => {
      if (typeof window.initCoverMediaSetup === "function") {
        window.initCoverMediaSetup();
      }
      if (typeof section._sojGalleryParallaxRefresh === "function") {
        section._sojGalleryParallaxRefresh();
      }
      if (window.SOJ_SMOOTH_SCROLL?.scheduleRefreshDimensions) {
        window.SOJ_SMOOTH_SCROLL.scheduleRefreshDimensions();
      } else if (typeof window.ScrollTrigger !== "undefined") {
        requestAnimationFrame(() => {
          window.ScrollTrigger.refresh();
        });
      }

      const gsap = window.gsap;
      if (gsap && newItems.length) {
        gsap.fromTo(
          newItems,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.4,
            stagger: 0.05,
            ease: "power2.out",
          },
        );
      }
    };

    const syncViewportItems = () => {
      const items = getItems();
      if (!paginated) {
        items.forEach((item) => item.classList.remove("is-mobile-hidden"));
        setHasMore(false);
        return;
      }

      if (!isMobile()) {
        items.forEach((item) => item.classList.remove("is-mobile-hidden"));
      } else {
        items.forEach((item, index) => {
          item.classList.toggle("is-mobile-hidden", index >= mobileRevealed);
        });
      }

      setHasMore(visibleCount() < foundPosts);
    };

    const fetchMore = () => {
      if (isFetching || !hasMore) {
        return;
      }

      isFetching = true;
      if (loadMoreButton) {
        loadMoreButton.disabled = true;
      }
      section.classList.add("is-loading");
      section.setAttribute("aria-busy", "true");

      const offset = getItems().length;
      const formData = new FormData();
      formData.append("action", section.dataset.action || "soj_projects_gallery_load_more");
      formData.append("nonce", section.dataset.nonce || "");
      formData.append("what_to_show", section.dataset.whatToShow || "all");
      formData.append("select_category", section.dataset.selectCategory || "");
      formData.append("selected_ids", section.dataset.selectedIds || "");
      formData.append("per_page", String(getPerPage()));
      formData.append("offset", String(offset));
      formData.append(
        "hide_project_pill",
        section.dataset.hideProjectPill === "1" ? "1" : "",
      );

      fetch(getAjaxUrl(section), {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Network response was not ok (${response.status})`);
          }
          return response.json();
        })
        .then((data) => {
          if (!data || !data.success) {
            throw new Error(data?.data || "Unexpected response");
          }

          const html = data.data.html || "";
          if (!html) {
            setHasMore(false);
            return;
          }

          const template = document.createElement("template");
          template.innerHTML = html.trim();
          const newItems = Array.from(template.content.children);
          newItems.forEach((item) => list.appendChild(item));

          if (typeof data.data.found_posts === "number") {
            foundPosts = data.data.found_posts;
            section.dataset.foundPosts = String(foundPosts);
          }

          if (isMobile()) {
            mobileRevealed += newItems.length;
          }

          syncViewportItems();
          refreshAfterChange(newItems);
        })
        .catch((error) => {
          console.error("[BLOCK] projects-gallery load more error:", error);
        })
        .finally(() => {
          isFetching = false;
          section.classList.remove("is-loading");
          section.removeAttribute("aria-busy");
          if (loadMoreButton) {
            loadMoreButton.disabled = !hasMore;
          }
        });
    };

    const handleLoadMore = () => {
      if (!hasMore || isFetching) {
        return;
      }

      if (isMobile()) {
        const hidden = getItems().filter((item) =>
          item.classList.contains("is-mobile-hidden"),
        );
        if (hidden.length) {
          const reveal = hidden.slice(0, perPageMobile);
          reveal.forEach((item) => item.classList.remove("is-mobile-hidden"));
          mobileRevealed += reveal.length;
          setHasMore(visibleCount() < foundPosts);
          refreshAfterChange(reveal);
          return;
        }
      }

      fetchMore();
    };

    if (loadMoreButton) {
      loadMoreButton.addEventListener("click", handleLoadMore);
    }

    syncViewportItems();

    let lastIsMobile = isMobile();
    window.addEventListener("resize", () => {
      const nowMobile = isMobile();
      if (nowMobile === lastIsMobile) {
        return;
      }
      lastIsMobile = nowMobile;
      if (!nowMobile) {
        mobileRevealed = Math.max(mobileRevealed, getItems().length);
      }
      syncViewportItems();
    });
  }

  function init() {
    document.querySelectorAll(".projects-gallery").forEach((section) => {
      initProjectsGalleryLoadMore(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/**
 * Cover media: in-view autoplay / hover / click (data-media-* attributes)
 */
(() => {
  if (document.body && document.body.classList.contains("wp-admin")) {
    return;
  }

  function initMediaSetup() {
    const mediaElements = document.querySelectorAll("[data-media-init]");
    if (!mediaElements.length) return;

    const pauseDelay = 200;
    /** Root margin % for IntersectionObserver: higher = start earlier before entering viewport */
    const viewportOffset = 0.1;
    const isHoverDevice = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;

    initMediaSetup._cleanup?.forEach((fn) => fn());
    const cleanupFns = [];
    const rootMarginValue = viewportOffset * 100;

    mediaElements.forEach((mediaEl) => {
      const video = mediaEl.querySelector("[data-media-video-src]");
      if (!video) return;

      const mode = mediaEl.dataset.mediaMode || "autoplay";
      const touchMode = mediaEl.dataset.mediaTouchMode;
      // Touch / coarse pointer: skip video entirely (poster image only).
      if (!isHoverDevice && touchMode === "none") {
        return;
      }
      const resetAttr = mediaEl.dataset.mediaReset;
      const pausedStatusAttr = mediaEl.dataset.mediaOnPause;
      const toggleElements = [...mediaEl.querySelectorAll("[data-media-toggle]")];

      const activeMode = !isHoverDevice
        ? touchMode || (mode === "hover" ? "autoplay" : mode)
        : mode;
      const shouldResetOnPause =
        resetAttr === "true"
          ? true
          : resetAttr === "false"
            ? false
            : activeMode === "hover";
      const pausedStatus =
        pausedStatusAttr === "paused" ? "paused" : "not-active";

      const clickTargets = toggleElements.length ? toggleElements : [mediaEl];
      const shouldUseClickToggle =
        activeMode === "click" ||
        (activeMode === "autoplay" && toggleElements.length);

      let isInView = false;
      let isHovering = false;
      let hasLoaded = false;
      let userPaused = false;
      let userActivated = false;
      let isActivated = false;
      let shouldBePlaying = false;
      let pauseTimer = null;

      const setStatus = (status) => {
        mediaEl.dataset.mediaStatus = status;
      };

      const clearPauseTimer = () => {
        clearTimeout(pauseTimer);
      };

      const addCleanup = (fn) => {
        cleanupFns.push(fn);
      };

      const on = (target, event, handler) => {
        target.addEventListener(event, handler);
        addCleanup(() => target.removeEventListener(event, handler));
      };

      const playAttempt = () => {
        video
          .play()
          .then(() => {
            if (shouldBePlaying) setStatus("playing");
          })
          .catch(() => {});
      };

      const loadVideo = () => {
        if (hasLoaded) return;

        const src = video.dataset.mediaVideoSrc;
        if (!src) return;

        video.muted = true;
        video.playsInline = true;
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.src = src;
        video.load();
        hasLoaded = true;
      };

      const shouldResume = () => {
        if (!isInView || document.hidden) return false;
        if (activeMode === "autoplay") return !userPaused;
        if (activeMode === "click") return userActivated && !userPaused;
        return isHovering;
      };

      const playVideo = () => {
        if (!isInView || document.hidden) return;

        shouldBePlaying = true;
        clearPauseTimer();
        loadVideo();
        setStatus(video.readyState < 3 ? "loading" : "playing");
        playAttempt();
      };

      const pauseVideo = (delay = 0, reset = false) => {
        shouldBePlaying = false;
        clearPauseTimer();

        pauseTimer = setTimeout(() => {
          video.pause();
          if (reset) video.currentTime = 0;
        }, delay);
      };

      const syncInViewFromRect = () => {
        const r = mediaEl.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        isInView =
          r.width > 0 &&
          r.height > 0 &&
          r.bottom > 0 &&
          r.top < vh &&
          r.right > 0 &&
          r.left < vw;
      };

      const handleHoverIn = () => {
        if (document.hidden) return;
        syncInViewFromRect();
        if (!isInView) return;

        isHovering = true;
        clearPauseTimer();

        if (!video.paused) {
          shouldBePlaying = true;
          setStatus("playing");
          return;
        }

        playVideo();
      };

      const handleHoverOut = () => {
        if (!isInView) return;

        isHovering = false;
        setStatus(pausedStatus);
        pauseVideo(pauseDelay, shouldResetOnPause);
      };

      const handleClick = () => {
        if (!isInView || document.hidden) return;

        clearPauseTimer();

        if (video.paused) {
          userActivated = true;
          userPaused = false;
          playVideo();
        } else {
          userActivated = true;
          userPaused = true;
          setStatus(pausedStatus);
          pauseVideo(pauseDelay, shouldResetOnPause);
        }
      };

      const handleViewport = (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== mediaEl) return;

          if (!isActivated && entry.isIntersecting) {
            isActivated = true;

            if (shouldUseClickToggle) {
              clickTargets.forEach((toggleEl) => on(toggleEl, "click", handleClick));
            }
          }

          isInView = entry.isIntersecting;

          if (isInView) {
            if (shouldResume()) playVideo();
          } else {
            isHovering = false;

            if (!video.paused || shouldBePlaying) {
              setStatus("paused");
              pauseVideo(0, false);
            }
          }
        });
      };

      const handlePageVisibilityChange = () => {
        if (document.hidden) {
          if (!video.paused || shouldBePlaying) {
            setStatus("paused");
            pauseVideo(0, false);
          }
          return;
        }
        if (shouldResume()) playVideo();
      };

      mediaEl.dataset.mediaStatus = "not-active";

      const observer = new IntersectionObserver(handleViewport, {
        rootMargin: `${rootMarginValue}% 0px ${rootMarginValue}% 0px`,
        threshold: 0,
      });

      observer.observe(mediaEl);

      if (activeMode === "hover") {
        // Use the full card as the hover zone so moving to titles/pills (siblings
        // of .cover-media) does not fire mouseleave on the media element alone.
        const hoverRoot = mediaEl.closest(".project-card") || mediaEl;
        on(hoverRoot, "mouseenter", handleHoverIn);
        on(hoverRoot, "mouseleave", handleHoverOut);
      }

      on(video, "playing", () => {
        if (shouldBePlaying) setStatus("playing");
      });
      on(video, "waiting", () => {
        if (shouldBePlaying) setStatus("loading");
      });
      on(video, "canplay", () => {
        if (shouldBePlaying && isInView && !document.hidden) playAttempt();
      });
      on(video, "loadeddata", () => {
        if (shouldBePlaying && isInView && !document.hidden) playAttempt();
      });
      on(video, "ended", () => {
        if (!shouldBePlaying || !isInView || document.hidden) return;
        video.currentTime = 0;
        playAttempt();
      });

      on(document, "visibilitychange", handlePageVisibilityChange);

      addCleanup(() => observer.disconnect());
      addCleanup(() => {
        clearPauseTimer();
        shouldBePlaying = false;
        video.pause();
      });
    });

    initMediaSetup._cleanup = cleanupFns;
  }

  function destroyCoverMediaSetup() {
    initMediaSetup._cleanup?.forEach((fn) => fn());
    initMediaSetup._cleanup = [];
  }

  window.destroyCoverMediaSetup = destroyCoverMediaSetup;
  window.initCoverMediaSetup = initMediaSetup;

  function bootMediaSetup() {
    initMediaSetup();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootMediaSetup);
  } else {
    bootMediaSetup();
  }
})();
